import type { FieldTransform, Operation, SchemaMutation, ValueMutation } from '../../../types';

// Pure display helpers for ChangeSet ops (SC-2524). Kept in a plain module (not
// the .tsx renderer) so the component file only exports components — the BE's
// `verb`/`describe` @properties aren't serialized onto the wire, so the FE
// re-derives the equivalent display strings here.

/**
 * The UPGRADE slate flavor (SC-2798) at the op level: the current value appears in
 * the candidate slate, so the model deems it VALID — "here's a better one," not
 * "this is wrong." Mirrors `isUpgradeSuggestion` (findingsOverlay) on the
 * per-entity diff side; the two surfaces must classify a finding identically.
 */
export function isUpgradeValueOp(op: Operation): boolean {
  return (
    op.type === 'value' &&
    op.before != null &&
    op.after != null &&
    op.before !== op.after &&
    op.candidates.includes(op.before)
  );
}

/**
 * Every op proposes an upgrade → the whole finding is an IMPROVEMENT: nothing is
 * wrong today, applying just makes it better. Drives the "✦ Suggested improvement"
 * pill and the "Apply improvement" button. Any non-upgrade op (a real correction,
 * a paired no-op, a transform/schema/enrich) keeps the fix vocabulary — calling a
 * finding an improvement when part of it corrects an error would undersell it.
 */
export function isImprovementChangeset(ops: Operation[]): boolean {
  return ops.length > 0 && ops.every(isUpgradeValueOp);
}

/**
 * A finding that only says "this column is mostly empty and worth filling": every
 * op is a WHOLE-COLUMN enrich (no entity subset), so there's no value to apply and
 * no decision to make yet — an opportunity, not a fix (SC-3444; the near-empty
 * column findings the profile pass raises, `auto_applicable` false). Calling it a
 * "Suggested fix" promised a fix nobody can apply.
 */
export function isOpportunityChangeset(ops: Operation[]): boolean {
  return ops.length > 0 && ops.every((op) => op.type === 'enrich' && op.entity_ids.length === 0);
}

/** Hover hint for the opportunity pill. */
export const OPPORTUNITY_TOOLTIP =
  'Worth filling in, but EKOM has no values for it yet — discuss it or fill it at the source.';

/** Popover for the improvement badge/pill. Deliberately outcome-neutral: the
 *  badge can sit on a MIXED card (an upgrade op beside a real correction) where
 *  no "Keep original" button exists — action guidance lives on the button, not
 *  here. */
export const IMPROVEMENT_TOOLTIP =
  'An improvement — the current value is also valid, and keeping it is a fine outcome.';

/** The always-visible keep-current note under an upgrade row. Informational
 *  only — on all-upgrade cards the "Keep original" button carries the how. */
export const KEEP_CURRENT_NOTE = 'the current value is also valid';

/** Title for the "Keep original" button (all-upgrade cards only) — it IS
 *  Acknowledge, relabeled in the card's own vocabulary so keeping the current
 *  value needs no workflow-concept translation (Erin, 2026-08-04). */
export const KEEP_ORIGINAL_TIP =
  'Keeps the current value and resolves this finding — stays on record.';

/** The no-op half of a paired repair (SC-2799), when a SIBLING op carries the
 *  actual change — worded so the row reads as part of the repair, not clutter. */
export const KEPT_AS_IS_NOTE = 'kept as is — the paired change carries this fix';
export const KEPT_AS_IS_TOOLTIP =
  'This value was verified correct — the paired change completes the repair; nothing changes here.';

/**
 * Map a ValueMutation's before/after to the FE `ValueDiff` verb — mirrors the BE
 * `ValueMutation.verb` property.
 */
export function valueVerb(op: ValueMutation): 'fill' | 'remove' | 'replace' {
  if (op.before === null) return 'fill';
  if (op.after === null) return 'remove';
  return 'replace';
}

const TRANSFORM_PHRASE: Record<FieldTransform['transform'], string> = {
  normalize_case: 'Normalize capitalization',
  trim: 'Trim surrounding whitespace',
  map_values: 'Map values to a standard set',
  normalize_units: 'Normalize units',
  normalize_date: 'Normalize the date format',
  normalize_delimiter: 'Normalize the delimiters',
  regex_replace: 'Apply a find-and-replace',
  set_constant: 'Set a constant value',
};

/** A `regex_replace` op read back into what it finds and what it writes (SC-3444). */
export interface FindReplace {
  /** The pattern as written — shown verbatim when it's a real regex. */
  pattern: string;
  replacement: string;
  /** The plain text the pattern matches, when it is plain text (optionally wrapped
   *  in `\\b…\\b`, with punctuation escaped as `\\.`); `null` for anything with
   *  regex syntax in it. */
  literal: string | null;
  /** True when the literal is matched only as a whole word (`\\b…\\b`). */
  wholeWord: boolean;
}

// A Python `\w` character under Unicode matching: any letter, digit or underscore.
const WORD_CHAR = /[\p{L}\p{N}_]/u;

// Characters with regex meaning when unescaped.
const REGEX_META = new Set('^$.|?*+()[]{}');

/** `core` as the plain text it matches, or null if it uses any regex syntax. An
 *  escaped punctuation mark (`\\.`, `\\(`) is that mark; an escaped letter or digit
 *  (`\\s`, `\\d`, `\\1`) is a regex class or reference, so it isn't plain text. */
function plainText(core: string): string | null {
  let out = '';
  for (let i = 0; i < core.length; i++) {
    const c = core[i];
    if (c === '\\') {
      const next = core[i + 1];
      if (next === undefined || WORD_CHAR.test(next)) return null;
      out += next;
      i++;
    } else if (REGEX_META.has(c)) {
      return null;
    } else {
      out += c;
    }
  }
  return out === '' ? null : out;
}

/** Read a `regex_replace` op's params, or `null` when they aren't the
 *  `{ pattern, replacement }` strings the backend requires. */
export function findReplaceOf(params: Record<string, unknown>): FindReplace | null {
  const { pattern, replacement } = params;
  if (typeof pattern !== 'string' || typeof replacement !== 'string' || pattern === '') return null;
  const wrapped = /^\\b(.*)\\b$/s.exec(pattern);
  let literal = plainText(wrapped ? wrapped[1] : pattern);
  // `\b` beside a word character means "not inside a word"; beside punctuation it
  // means the opposite (a word character MUST be there). Only the first reads as
  // "the word …", so anything else stays a regex.
  if (wrapped && literal !== null && !(WORD_CHAR.test(literal[0]) && WORD_CHAR.test(literal[literal.length - 1]))) {
    literal = null;
  }
  return { pattern, replacement, literal, wholeWord: literal !== null && wrapped !== null };
}

/** "Replace the word “cutler” with “cutlery”", "Remove “TBD”" — or, for a real
 *  regex, the pattern itself: "Find /^N\/A$/ → replace with “”". Never
 *  paraphrases a regex. */
export function findReplacePhrase(fr: FindReplace): string {
  if (fr.literal !== null) {
    const what = `${fr.wholeWord ? 'the word ' : ''}“${fr.literal}”`;
    return fr.replacement === '' ? `Remove ${what}` : `Replace ${what} with “${fr.replacement}”`;
  }
  return `Find /${fr.pattern}/ → replace with “${fr.replacement}”`;
}

// Python's `\b` is Unicode-aware (a word character is any letter, digit or `_`),
// JS's is ASCII-only — so a whole-word match is spelled out with Unicode classes
// rather than reusing the pattern, or "café" would split at the "é".
const WORD = '[\\p{L}\\p{N}_]';

/** Characters of context kept either side of the first change in an excerpt. */
const EXCERPT_CONTEXT = 24;

/**
 * A short before → after excerpt of what a plain-text find-and-replace does to
 * `value` — centered on the first match, with `…` where text was cut. `null` when
 * the pattern is a real regex (only the backend's Python engine can say what it
 * does; a JS re-run could disagree), when the replacement could hold a Python
 * group reference (`\1`, `\g<…>`), or when nothing in `value` matches.
 * Case-sensitive and every occurrence, like the backend's `re.sub`.
 */
export function findReplaceExcerpt(
  value: string,
  fr: FindReplace,
): { before: string; after: string } | null {
  if (fr.literal === null || fr.replacement.includes('\\')) return null;
  const escaped = fr.literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const source = fr.wholeWord ? `(?<!${WORD})${escaped}(?!${WORD})` : escaped;
  const re = new RegExp(source, 'gu');
  const first = re.exec(value);
  if (!first) return null;
  // A function replacer, so a `$` in the replacement is written literally (as
  // Python writes it) instead of being read as a JS substitution pattern.
  const after = value.replace(re, () => fr.replacement);
  const start = Math.max(0, first.index - EXCERPT_CONTEXT);
  const lead = start > 0 ? '…' : '';
  const beforeEnd = Math.min(value.length, first.index + fr.literal.length + EXCERPT_CONTEXT);
  // The tail of the after-string shifts by the length difference of every
  // replacement up to that point; clip it at the same distance past the first
  // replacement so both excerpts cover the same stretch of text.
  const afterEnd = Math.min(after.length, first.index + fr.replacement.length + EXCERPT_CONTEXT);
  return {
    before: `${lead}${value.slice(start, beforeEnd)}${beforeEnd < value.length ? '…' : ''}`,
    after: `${lead}${after.slice(start, afterEnd)}${afterEnd < after.length ? '…' : ''}`,
  };
}

/**
 * A short human phrase for a FieldTransform — mirrors the BE `FieldTransform.describe()`,
 * except that a find-and-replace says what it finds and writes (SC-3444).
 * Falls back to the humanized rule name for any transform not in the phrase map.
 */
export function transformPhrase(op: FieldTransform): string {
  if (op.transform === 'regex_replace') {
    const fr = findReplaceOf(op.params);
    if (fr) return findReplacePhrase(fr);
  }
  const base = TRANSFORM_PHRASE[op.transform] ?? op.transform.replace(/_/g, ' ');
  const style = op.params.style;
  if (op.transform === 'normalize_case' && typeof style === 'string') {
    return `${base} (${style})`;
  }
  const value = op.params.value;
  if (op.transform === 'set_constant' && typeof value === 'string') {
    return `Set to “${value}”`;
  }
  return base;
}

/** One run of a "can't apply" line: plain text, or a field name rendered as code. */
export type PhrasePart = string | { code: string };

/** The fields a split proposes — `SchemaPreview`'s filtering, so card + note agree. */
export function splitTargets(op: SchemaMutation): string[] {
  return Array.isArray(op.params.split_into)
    ? (op.params.split_into as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];
}

/**
 * What a structural op would do, in words (SC-3257) — "Split `categories` into 4
 * fields", "Rename `x` to `y`", "Remove `x`", "Add `x`". Says what the change IS,
 * never when it'll be applyable.
 */
export function schemaOpPhrase(op: SchemaMutation): PhrasePart[] {
  const field = { code: op.field };
  switch (op.op) {
    case 'split': {
      const n = splitTargets(op).length;
      return ['Split ', field, n > 0 ? ` into ${n} fields` : ' into separate fields'];
    }
    case 'rename': {
      const to = typeof op.params.to === 'string' && op.params.to ? op.params.to : null;
      return to ? ['Rename ', field, ' to ', { code: to }] : ['Rename ', field];
    }
    case 'remove':
      return ['Remove ', field];
    case 'add':
      return ['Add ', field];
  }
}

/** The closing line on every card whose Apply is hidden (SC-3257). No dates. */
export const CANT_APPLY_NOTE = "EKOM can't apply this change automatically yet.";

/**
 * The lines describing a finding EKOM can't apply (`auto_applicable` false): one
 * phrase per structural op, then {@link CANT_APPLY_NOTE}. A non-schema case
 * (unresolved enrich, a transform that won't build) gets the closing line alone —
 * the card body already shows the change, and we promise nothing about it.
 */
export function cantApplyLines(ops: Operation[]): PhrasePart[][] {
  const schema = ops.filter((op): op is SchemaMutation => op.type === 'schema');
  return dedupeLines([...schema.map(schemaOpPhrase), [CANT_APPLY_NOTE]]);
}

/** Drop repeated lines (same text) — a repeat says nothing new, and the note keys
 *  its lines by text. */
export function dedupeLines(lines: PhrasePart[][]): PhrasePart[][] {
  const seen = new Set<string>();
  return lines.filter((parts) => {
    const text = phraseText(parts);
    if (seen.has(text)) return false;
    seen.add(text);
    return true;
  });
}

/** A phrase as plain text (fields unquoted) — for titles / assertions. */
export function phraseText(parts: PhrasePart[]): string {
  return parts.map((p) => (typeof p === 'string' ? p : p.code)).join('');
}

/** One value op's display identity — ops with equal signatures propose the same
 *  change (JSON keeps null/'' distinct and arrays ordered; collision-proof). */
export function valueOpSignature(op: ValueMutation): string {
  return JSON.stringify([op.before, op.after, op.candidates]);
}

/**
 * The worked-example decision (SC-2908, Erin's case-3/4 treatment): a changeset
 * whose value ops span MULTIPLE entities with at least one field proposing
 * DIFFERENT changes per entity renders as ONE entity's grouped ops (pairing
 * visible) + "N−1 more follow this pattern" — never the unattributed matrix.
 *
 * Returns the swappable entity ids (op order, first-seen) — or null when the
 * card should keep its existing treatment: single-entity findings render every
 * op, and multi-entity IDENTICAL ops keep the collapsed row (case 2).
 */
export function workedExampleEntityIds(ops: Operation[]): string[] | null {
  const byField = new Map<string, ValueMutation[]>();
  const entityIds: string[] = [];
  for (const op of ops) {
    if (op.type !== 'value') continue;
    const group = byField.get(op.field);
    if (group) group.push(op);
    else byField.set(op.field, [op]);
    if (!entityIds.includes(op.entity_id)) entityIds.push(op.entity_id);
  }
  if (entityIds.length <= 1) return null;
  const anyFieldDiffers = [...byField.values()].some((group) =>
    group.some((op) => valueOpSignature(op) !== valueOpSignature(group[0])),
  );
  return anyFieldDiffers ? entityIds : null;
}

/** The ops the worked example shows for `entityId`: its own value ops plus the
 *  field-level ops that actually cover it, in changeset order — the per-entity
 *  grouping that keeps a paired repair visibly paired. Transforms/enrichments
 *  can target an entity SUBSET (empty `entity_ids` = whole field), so a subset
 *  op only renders under entities it touches — showing it under others would
 *  claim a change that won't happen (Greptile P1). Schema ops are structural
 *  (whole column) and always apply. */
export function opsForEntity(ops: Operation[], entityId: string): Operation[] {
  return ops.filter((op) => {
    if (op.type === 'value') return op.entity_id === entityId;
    if (op.type === 'transform' || op.type === 'enrich') {
      return op.entity_ids.length === 0 || op.entity_ids.includes(entityId);
    }
    return true;
  });
}
